/* eslint-disable no-param-reassign */
import React, {
  forwardRef,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import ct from "class-types.macro";
import Department from "./Department";
import DepartmentJobs from "./DepartmentJobs";
import JobListing from "./JobListing";
import JobsLocationsList from "./JobsLocationsList";
import { Filter } from "./Filter";

interface Props {
  jobListings: JobListing[];
  department: Department;
  className?: string;
  topRightButton?: ReactElement;
  initialStateSelected?: string;
}

const SingleDepartmentJobBrowser = forwardRef<HTMLDivElement, Props>(
  (
    {
      jobListings,
      department,
      className = "",
      topRightButton,
      initialStateSelected,
    },
    ref
  ) => {
    const [stateNames, setStateNames] = useState<string[]>([]);
    const initialLoadComplete = useRef(false);
    const workingLocationIdRemote = 2;

    // Things to filter by
    const [filter, setFilter] = useState<Filter | undefined>(undefined);

    // Things that get filtered
    const [filteredJobListings, setFilteredJobListings] = useState<
      JobListing[]
    >(jobListings);

    // Filter states
    useEffect(() => {
      const newStates = jobListings.reduce<string[]>((allLocations, job) => {
        if (!allLocations.includes(job.stateName)) {
          allLocations.push(job.stateName);
        }

        return allLocations;
      }, []);

      setStateNames(newStates.sort());
    }, [jobListings]);

    // Filter jobs
    useEffect(() => {
      const newFilteredJobs = jobListings.filter((job) => {
        const matchesStateFilter =
          filter === undefined ||
          job.stateName.toUpperCase() === filter.state?.toUpperCase();

        return (
          matchesStateFilter ||
          (job.workingLocationId === workingLocationIdRemote &&
            filter?.allowRemote)
        );
      });

      setFilteredJobListings(newFilteredJobs);
    }, [filter, jobListings]);

    useEffect(() => {
      if (!initialLoadComplete.current) {
        return;
      }

      setFilter({ allowRemote: false, state: initialStateSelected });
    }, [initialStateSelected]);

    useEffect(() => {
      if (!initialLoadComplete.current) {
        return;
      }

      if (filter?.state) {
        window.history.replaceState(
          null,
          window.document.title,
          `?location=${filter.state.toLowerCase()}`
        );
      } else {
        window.history.replaceState(
          null,
          window.document.title,
          window.location.pathname
        );
      }
    }, [filter]);

    useEffect(() => {
      initialLoadComplete.current = true;
    }, []);

    return (
      <div
        id="careers-positions"
        className={`careers-positions ${className}`}
        ref={ref}
      >
        <div className={ct("container")}>
          <div className="careers-positions-top">
            {topRightButton}
            <h2 className={`careers-positions__title ${ct("pt-2")}`}>
              {department.name} Jobs
            </h2>
            <div className="careers-positions__separator" />
            <div className="text">
              Have you got what it takes? We&apos;re looking for driven,
              forward-thinking people.
            </div>
          </div>
          {filteredJobListings.length === 0 ? (
            <div className={ct("text-gray-600", "w-full")}>
              No Current Openings
            </div>
          ) : (
            <>
              <div className="careers-positions-left">
                <JobsLocationsList
                  stateNames={stateNames}
                  onFilter={setFilter}
                  initialLocationSelected={initialStateSelected}
                  jobListings={jobListings}
                />
              </div>
              <div className="careers-positions-right">
                <DepartmentJobs
                  jobListings={filteredJobListings}
                  department={department}
                  displayHeader={false}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
);

export default SingleDepartmentJobBrowser;
